import { useState } from "react";
import type { ContentField } from "@/types/template";

interface Props {
  field: ContentField;
  onSave: (updated: ContentField) => void;
  onCancel: () => void;
}

type PatchFn = (changes: object) => void;

function LabelRow({ draft, patch }: Readonly<{ draft: ContentField; patch: PatchFn }>) {
  if (draft.type === "sub-template") {
    return null;
  }
  return (
    <div className="field-def-row">
      <label>Label</label>
      <input
        type="text"
        value={draft.label}
        onChange={(e) => {
          patch({ label: e.target.value });
        }}
      />
    </div>
  );
}

function TextFields({ draft, patch }: Readonly<{ draft: ContentField; patch: PatchFn }>) {
  if (draft.type !== "text") {
    return null;
  }
  return (
    <>
      <div className="field-def-row">
        <label>Default value</label>
        <input
          type="text"
          value={draft.defaultValue}
          onChange={(e) => {
            patch({ defaultValue: e.target.value });
          }}
        />
      </div>
      <div className="field-def-row field-def-row-checks">
        <label>
          <input
            type="checkbox"
            checked={draft.multiline}
            onChange={(e) => {
              patch({ multiline: e.target.checked });
            }}
          />
          Multiline
        </label>
        <label>
          <input
            type="checkbox"
            checked={draft.optional}
            onChange={(e) => {
              patch({ optional: e.target.checked });
            }}
          />
          Optional
        </label>
      </div>
    </>
  );
}

function SelectFields({ draft, patch }: Readonly<{ draft: ContentField; patch: PatchFn }>) {
  if (draft.type !== "select") {
    return null;
  }
  return (
    <div className="field-def-row">
      <label>Options (one per line)</label>
      <textarea
        value={draft.options.join("\n")}
        rows={5}
        onChange={(e) => {
          patch({ options: e.target.value.split("\n") });
        }}
      />
    </div>
  );
}

function ListFields({ draft, patch }: Readonly<{ draft: ContentField; patch: PatchFn }>) {
  if (draft.type !== "checkbox-list" && draft.type !== "statement-list") {
    return null;
  }
  return (
    <>
      <div className="field-def-row">
        <label>
          {draft.type === "checkbox-list"
            ? "Items (one per line)"
            : "Default statements (one per line)"}
        </label>
        <textarea
          value={draft.items.join("\n")}
          rows={5}
          onChange={(e) => {
            patch({ items: e.target.value.split("\n") });
          }}
        />
      </div>
      {draft.type === "checkbox-list" && (
        <div className="field-def-row field-def-row-checks">
          <label>
            <input
              type="checkbox"
              checked={draft.defaultAll ?? false}
              onChange={(e) => {
                patch({ defaultAll: e.target.checked });
              }}
            />
            Default select all
          </label>
        </div>
      )}
    </>
  );
}

function parseScoreOptionsText(text: string) {
  return text
    .split("\n")
    .map((line) => {
      const [v, ...rest] = line.trim().split(/\s+/);
      return { label: rest.join(" "), value: Number(v) };
    })
    .filter((o) => !Number.isNaN(o.value));
}

function ScoreFields({ draft, patch }: Readonly<{ draft: ContentField; patch: PatchFn }>) {
  if (draft.type !== "score") {
    return null;
  }
  return (
    <>
      <div className="field-def-row">
        <label>Max score</label>
        <input
          type="number"
          value={draft.max}
          style={{ width: 80 }}
          onChange={(e) => {
            patch({ max: Number(e.target.value) });
          }}
        />
      </div>
      {draft.scoreOptions && (
        <div className="field-def-row">
          <label>
            Score options (one per line: <code>value label</code>)
          </label>
          <textarea
            value={draft.scoreOptions.map((o) => `${o.value} ${o.label}`).join("\n")}
            rows={5}
            onChange={(e) => {
              patch({ scoreOptions: parseScoreOptionsText(e.target.value) });
            }}
          />
        </div>
      )}
    </>
  );
}

function GridFields({ draft, patch }: Readonly<{ draft: ContentField; patch: PatchFn }>) {
  if (draft.type !== "grid") {
    return null;
  }
  return (
    <>
      <div className="field-def-row">
        <label>Columns (one per line)</label>
        <textarea
          value={draft.columns.join("\n")}
          rows={3}
          onChange={(e) => {
            patch({ columns: e.target.value.split("\n") });
          }}
        />
      </div>
      <div className="field-def-row">
        <label>Row labels (one per line)</label>
        <textarea
          value={draft.rows.map((r) => r.label).join("\n")}
          rows={3}
          onChange={(e) => {
            patch({ rows: e.target.value.split("\n").map((label) => ({ defaults: {}, label })) });
          }}
        />
      </div>
    </>
  );
}

function DateFields({ draft, patch }: Readonly<{ draft: ContentField; patch: PatchFn }>) {
  if (draft.type !== "date") {
    return null;
  }
  return (
    <>
      <div className="field-def-row">
        <label>Default date</label>
        <input
          type="date"
          value={draft.defaultValue}
          onChange={(e) => {
            patch({ defaultValue: e.target.value });
          }}
        />
      </div>
      <div className="field-def-row field-def-row-checks">
        <label>
          <input
            type="checkbox"
            checked={draft.optional}
            onChange={(e) => {
              patch({ optional: e.target.checked });
            }}
          />
          Optional
        </label>
      </div>
    </>
  );
}

function SubTemplateFields({ draft }: Readonly<{ draft: ContentField }>) {
  if (draft.type !== "sub-template") {
    return null;
  }
  return (
    <div className="field-def-row">
      <label>Sub-template ID</label>
      <input type="text" value={draft.templateId} readOnly style={{ opacity: 0.6 }} />
    </div>
  );
}

export function FieldDefEditor({ field, onCancel, onSave }: Readonly<Props>) {
  const [draft, setDraft] = useState<ContentField>(() => structuredClone(field));

  const patch: PatchFn = (changes) => {
    setDraft((prev) => ({ ...prev, ...changes }) as ContentField);
  };

  return (
    <div className="field-def-editor">
      <div className="field-def-editor-header">
        <span className="field-def-type-badge">{draft.type}</span>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            className="primary"
            onClick={() => {
              onSave(draft);
            }}
          >
            Done
          </button>
          <button onClick={onCancel}>Cancel</button>
        </div>
      </div>
      <LabelRow draft={draft} patch={patch} />
      <TextFields draft={draft} patch={patch} />
      <SelectFields draft={draft} patch={patch} />
      <ListFields draft={draft} patch={patch} />
      <ScoreFields draft={draft} patch={patch} />
      <GridFields draft={draft} patch={patch} />
      <DateFields draft={draft} patch={patch} />
      <SubTemplateFields draft={draft} />
    </div>
  );
}
